import React, { Fragment, useEffect, useState } from 'react'
import { connect } from 'react-redux'
import SectionItem from '@components/Layout/SectionItem/SectionItem'
import './Question.scss'
import { LuxSelect, LuxRichInputText, LuxInputSwitch, LuxButton } from '@lux/wardrobe-lib'
import { useIntl } from 'react-intl'
import SectionItemContainer from '@/app/components/Layout/SectionItemContainer/SectionItemContainer'
import AddAttachment from '@components/UI/AddAttachment/AddAttachment'
import Answer from '../Answer/Answer'
import { setQuestionStatus } from '@/app/redux/actions/activity-creation'
import { Question as IQuestion } from '@/app/models/new-activity-creation-state'
import { ProjectApiQuestionType } from '@/app/models/projects-service'
import { ProjectsService } from '@/app/services/ProjectsService'

const typeOptions = [
    {
        value: ProjectApiQuestionType.TEXT,
        label: 'modals.new.activity.section.action.question.type.text',
    },
    {
        value: ProjectApiQuestionType.SINGLE_CHOICE,
        label: 'modals.new.activity.section.action.question.type.single',
    },
    {
        value: ProjectApiQuestionType.MULTI_CHOICE,
        label: 'modals.new.activity.section.action.question.type.multiple',
    },
    {
        value: ProjectApiQuestionType.NUMERIC,
        label: 'modals.new.activity.section.action.question.type.numeric',
    },
    {
        value: ProjectApiQuestionType.YESNO,
        label: 'modals.new.activity.section.action.question.type.yesno',
    },
    {
        value: ProjectApiQuestionType.DATE,
        label: 'modals.new.activity.section.action.question.type.date',
    },
]

const Question = (props) => {
    const componentClass = 'smart-new-activity-action-question'
    const { form, title, onQuestionSave, onReduce, onUpdateAnswer, onRemoveAnswer, disabled } =
        props
    const intl = useIntl()
    const [showAnswer, setShowAnswer] = useState(false)
    const [text, setText] = useState(form.text)

    useEffect(() => {
        setShowAnswer(
            form.type === ProjectApiQuestionType.SINGLE_CHOICE ||
                form.type === ProjectApiQuestionType.MULTI_CHOICE
        )
    }, [])

    useEffect(() => {
        onQuestionSave({ text })
    }, [text])

    const onSave = () => {
        onQuestionSave({ isDone: true, isEdit: false })
    }
    const onUpload = (files) => {
        onQuestionSave({
            attachmentList: [
                ...form.attachmentList,
                ...files.map((file: any) => ({
                    file: file.file,
                    url: file.url,
                    id: file.id,
                })),
            ],
        })
    }

    const onRemoveAttachment = (file) => {
        let newList = [...form.attachmentList]
        const fileIndex = newList.findIndex((i) => i.file.name === file.name)
        newList.splice(fileIndex, 1)
        onQuestionSave({
            attachmentList: [...newList],
        })
    }

    const onType = (value) => {
        setShowAnswer(
            value === ProjectApiQuestionType.SINGLE_CHOICE ||
                value === ProjectApiQuestionType.MULTI_CHOICE
        )
        onQuestionSave({ type: value })
    }

    const isQuestionDone = () => {
        switch (form.type) {
            case ProjectApiQuestionType.TEXT:
            case ProjectApiQuestionType.DATE:
            case ProjectApiQuestionType.NUMERIC:
            case ProjectApiQuestionType.YESNO:
                return !!form.text
            case ProjectApiQuestionType.SINGLE_CHOICE:
            case ProjectApiQuestionType.MULTI_CHOICE:
                return (
                    !!form.text &&
                    form.answerList.length > 0 &&
                    !form.answerList.filter((a) => !a?.value.length).length
                )
        }
    }

    const ctaTemplate = (
        <Fragment>
            <div
                className="link-mini"
                onClick={() =>
                    !disabled &&
                    onQuestionSave({
                        type: null,
                        text: '',
                        attachmentList: [],
                        mandatoryAttachment: false,
                        answerList: [],
                    })
                }>
                {intl.formatMessage({
                    id: 'modals.new.activity.section.clear',
                    defaultMessage: 'clear',
                })}
            </div>
            <LuxButton
                onClick={onSave}
                text={intl.formatMessage({
                    id: 'modals.new.activity.section.action.question.save',
                    defaultMessage: 'Save question',
                })}
                disabled={disabled || !isQuestionDone()}
                variant="outlined"
            />
        </Fragment>
    )

    return (
        <SectionItemContainer ctaTemplate={ctaTemplate}>
            <SectionItem>
                <div className={`${componentClass}__item-title`} onClick={() => onReduce()}>
                    <span className={`${componentClass}__item-title-reduce`}>-</span>
                    <span className="link-mini">{title}</span>
                </div>
            </SectionItem>
            <SectionItem>
                <div className={`${componentClass}__type`}>
                    <LuxSelect
                        value={form.type}
                        disabled={disabled}
                        mandatory={true}
                        onChangeCallback={onType}
                        label={intl.formatMessage({
                            id: 'modals.new.activity.section.action.question.type',
                            defaultMessage: 'Question type',
                        })}
                        placeholder={intl.formatMessage({
                            id: 'modals.new.activity.section.action.question.type.placeholder',
                            defaultMessage: 'Type',
                        })}
                        options={typeOptions.map((opt) => ({
                            ...opt,
                            label: intl.formatMessage({
                                id: opt.label,
                                defaultMessage: 'Question type',
                            }),
                        }))}
                        additionalProps={{
                            appendTo: 'self',
                        }}
                    />
                </div>
            </SectionItem>
            <div className={`${componentClass}__field-container`}>
                <LuxRichInputText
                    value={text}
                    mandatory={true}
                    onChangeCallback={(value) => setText(value)}
                    label={intl.formatMessage({
                        id: 'modals.new.activity.section.action.question.text',
                        defaultMessage: 'Question text',
                    })}
                    placeholder={intl.formatMessage({
                        id: 'modals.new.activity.section.action.question.text.placeholder',
                        defaultMessage: 'Type here',
                    })}
                    config={{
                        events: {
                            initialized() {
                                const editor = this
                                if (disabled) editor.edit.off()
                            },
                        },
                    }}
                    fileUploadUrl={ProjectsService.FROALA_UPLOAD_URL}
                />
            </div>
            <div className={`${componentClass}__field-container`}>
                <AddAttachment
                    id={`question-${Math.random() * 100}`}
                    showSplitFile={false}
                    form={{ attachmentList: { value: form.attachmentList } }}
                    onUpload={(form) => onUpload(form)}
                    showRemoveIcon
                    onRemove={(file) => onRemoveAttachment(file)}
                    disabled={disabled}
                />
            </div>
            {showAnswer && (
                <Answer
                    answerList={form.answerList}
                    onUpdateAnswer={(index, answer, disabled) =>
                        onUpdateAnswer(index, answer, disabled)
                    }
                    onRemoveAnswer={(index) => onRemoveAnswer(index)}
                    disabled={disabled}
                />
            )}
            <LuxInputSwitch
                label={intl.formatMessage({
                    id: 'modals.new.activity.section.action.question.mandatory.attachment',
                    defaultMessage: 'Mandatory attachment',
                })}
                value={form.mandatoryAttachment}
                onChangeCallback={(value: boolean) =>
                    onQuestionSave({ mandatoryAttachment: value })
                }
                additionalProps={{ disabled }}
            />
        </SectionItemContainer>
    )
}

const mapStateToProps = (state: any) => ({})

const mapDispatchToProps = (dispatch: any) => ({
    setQuestionStatus: (index: number, question: Partial<IQuestion>) =>
        dispatch(setQuestionStatus(index, question)),
})

export default connect(mapStateToProps, mapDispatchToProps)(Question)
